// Product page (03 Product, Sticky CRO S2/S3): variant picking, prices and CTA labels, the gallery, the tier popup and the sticky buy bar.

export function resolveVariant(variants, options) {
  return variants.find(variant => variant.options.length === options.length
    && variant.options.every((value, index) => value === options[index])) || null;
}

export function initialVariant(variants, requestedId) {
  return variants.find(variant => requestedId && String(variant.id) === String(requestedId))
    || variants.find(variant => variant.available) || variants[0] || null;
}

// Like Shopify's money_without_trailing_zeros: $850, $849.50.
export function formatMoney(cents, currency = 'USD') {
  const value = Number(cents);
  if (!Number.isFinite(value)) return '';
  const whole = value % 100 === 0;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 }).format(value / 100);
}

export function fill(template, values) {
  return String(template ?? '').replace(/\{(\w+)\}/g, (match, key) => (key in values ? String(values[key]) : match));
}

// The price Shopify charges for a variant: its selling plan allocation when the page sells through a plan, else the variant price.
export function sellingPrice(variant, planId) {
  if (!variant) return null;
  if (planId) {
    const price = variant.plans?.[String(planId)];
    return Number.isFinite(price) ? price : null;
  }
  return Number.isFinite(variant.price) ? variant.price : null;
}

// What the page can sell for the variant on screen. A plan page sells only through its open plan:
// with no tier open, or on a variant without that plan, nothing is for sale, and the list price is never a fallback.
// A back-order page is different: the Worker attaches a Back-order plan group to each variant on its own, so every
// variant names its own plan (variant.backorder.plan). A variant that carries one sells through it, with its ship date;
// one that does not sells at its list price.
export function buyState(variant, mode, planId) {
  if (variant && mode === 'backorder') {
    const own = variant.backorder?.plan ? String(variant.backorder.plan) : null;
    const planPrice = own ? sellingPrice(variant, own) : null;
    const price = planPrice ?? sellingPrice(variant, null);
    const plan = planPrice !== null;
    if (price === null) return { price: null, buyable: false, reason: 'unavailable', plan: false, planId: null, description: '' };
    return { price, buyable: Boolean(variant.available), reason: variant.available ? 'ok' : 'sold_out', plan, planId: plan ? own : null, description: plan ? variant.backorder.description || '' : '' };
  }
  if (!variant || (mode !== 'plan' && mode !== 'list')) return { price: null, buyable: false, reason: 'unavailable' };
  if (mode === 'plan' && !planId) return { price: null, buyable: false, reason: 'sold_out' };
  const price = sellingPrice(variant, mode === 'plan' ? planId : null);
  if (price === null) return { price: null, buyable: false, reason: 'unavailable' };
  return variant.available ? { price, buyable: true, reason: 'ok' } : { price, buyable: false, reason: 'sold_out' };
}

// Back-order page, per variant: the plan posted with the form, the back-order note and its ship date, and the in-stock
// parts (in-stock line, delivery estimate, code row, honest note) that a back-order variant must not show.
export function paintBackorder(root, form, sale, variant) {
  const input = form?.querySelector('input[name="selling_plan"]');
  if (input) {
    input.disabled = !sale.plan;
    if (sale.planId) input.value = sale.planId;
  }
  for (const node of root.querySelectorAll('[data-ns-backorder]')) node.hidden = !sale.plan;
  for (const node of root.querySelectorAll('[data-ns-backorder-text]')) node.textContent = sale.description || node.dataset.default || '';
  for (const node of root.querySelectorAll('[data-ns-not-backorder]')) node.hidden = sale.plan || (node.hasAttribute('data-ns-instock') && !variant?.available);
}

// The CTA texts for the variant on screen: a list-price variant on a back-order page uses the list templates.
export function ctaTemplates(button, shortNode, listOnly) {
  return {
    template: (listOnly && button.dataset.templateList) || button.dataset.template,
    short: shortNode ? (listOnly && shortNode.dataset.labelList) || shortNode.dataset.label : undefined,
  };
}

// What a tier saves against the variant's list price, or null when the variant does not carry that plan.
export function planSaving(variant, planId) {
  const price = sellingPrice(variant, planId);
  return price === null || !Number.isFinite(variant.price) ? null : variant.price - price;
}

// The combo row under the product: the product at its current price plus the in-stock picks; the bundle saving counts the picks only.
export function comboTotals(main, picks, percent) {
  if (!Number.isFinite(main)) return null;
  const pickTotal = Number(picks) || 0;
  const picksNow = Math.floor((pickTotal * (100 - (Number(percent) || 0)) + 50) / 100);
  return { was: main + pickTotal, now: main + picksNow, save: pickTotal - picksNow };
}

// The button labels for a buy state: the long one carries the price, the short phone one (sticky bar) does not.
// On a page not sold to the shopper's country (data-elsewhere, owner 2026-10-29) a closed variant says so, never Sold out:
// Shopify reports every variant unavailable there, so "sold out" would be untrue.
export function ctaText({ price, buyable, reason }, { template, short, soldOut, unavailable, elsewhere, elsewhereShort }, currency = 'USD') {
  const closed = reason === 'sold_out' ? elsewhere || soldOut : unavailable;
  const closedShort = reason === 'sold_out' && elsewhere ? elsewhereShort || elsewhere : closed;
  return {
    long: buyable ? fill(template, { price: price === null ? '' : formatMoney(price, currency) }) : closed,
    short: short === undefined || short === null ? null : buyable ? short : closedShort,
  };
}

// Business days from now: an order placed on Friday with min 2 arrives Tuesday at the earliest.
export function addBusinessDays(fromMs, days) {
  const date = new Date(fromMs);
  let left = Math.max(0, Math.floor(days));
  while (left > 0) {
    date.setDate(date.getDate() + 1);
    const day = date.getDay();
    if (day !== 0 && day !== 6) left -= 1;
  }
  return date;
}

export function formatRange(from, to, locale = 'en-US') {
  const month = date => date.toLocaleDateString(locale, { month: 'short' });
  if (from.toDateString() === to.toDateString()) return `${month(from)} ${from.getDate()}`;
  if (from.getMonth() === to.getMonth() && from.getFullYear() === to.getFullYear()) return `${month(from)} ${from.getDate()}–${to.getDate()}`;
  return `${month(from)} ${from.getDate()} – ${month(to)} ${to.getDate()}`;
}

// The warehouse starts on an order on the day of the next cutoff (shop custom.ship_cutoff; live: 19:00 Denver, Mon–Fri):
// today before it, else the next ship day. An order after the cutoff, or on a weekend, so counts one more business day
// (2026-10-27, owner: "the delivery time has to be accurate"). Without a cutoff the count starts today, as before.
export function etaWait(nowMs, cutoff) {
  const time = /^(\d{1,2}):(\d{2})$/.exec(typeof cutoff?.time === 'string' ? cutoff.time.trim() : '');
  if (!time || !Array.isArray(cutoff.days) || !cutoff.days.length) return 0;
  let parts;
  try { parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', { timeZone: cutoff.tz, hourCycle: 'h23', weekday: 'short', hour: 'numeric', minute: 'numeric' }).formatToParts(new Date(nowMs)).map(part => [part.type, part.value])); }
  catch { return 0; }
  const weekday = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(parts.weekday);
  const before = Number(parts.hour) * 60 + Number(parts.minute) < Number(time[1]) * 60 + Number(time[2]);
  return cutoff.days.includes(weekday) && before ? 0 : 1;
}

// A template's text around its {range} (the template's own placeholders are filled first).
export function etaParts(template, range) {
  const text = String(template ?? '');
  const at = text.indexOf('{range}');
  return at < 0 ? [fill(text, {}), '', ''] : [fill(text.slice(0, at), {}), range, fill(text.slice(at + 7), {})];
}

export function etaLabel(nowMs, minDays, maxDays, locale = 'en-US', cutoff = null) {
  const wait = etaWait(nowMs, cutoff);
  const min = Math.max(0, Number(minDays) || 0) + wait;
  const max = Math.max(min, (Number(maxDays) || 0) + wait);
  return formatRange(addBusinessDays(nowMs, min), addBusinessDays(nowMs, max), locale);
}

// The sticky bar shows once the buy-box button has scrolled above the top of the window, or under the pinned header
// (ns-header.js writes its height to --ns-sticky-offset; 0 on phones), as the prototype's bar does.
export const pastButton = (rect, top = 0) => Boolean(rect) && rect.height > 0 && Number.isFinite(top) && rect.bottom < top;

const readAttrJson = text => {
  try { return text ? JSON.parse(text) : null; } catch { return null; }
};

const readJson = node => {
  try { return JSON.parse(node?.textContent || 'null'); } catch { return null; }
};

if (typeof customElements !== 'undefined' && !customElements.get('ns-product')) {
  customElements.define('ns-product', class extends HTMLElement {
    connectedCallback() {
      this.data = readJson(this.querySelector('script[data-ns-product]')) || { variants: [] };
      this.form = this.querySelector('form.ns-buy__form');
      this.onChange = event => { if (event.target.closest('[data-ns-option]')) this.pick(); };
      this.onClick = event => {
        const step = event.target.closest('[data-ns-step]');
        if (step) this.step(Number(step.dataset.nsStep));
        const copy = event.target.closest('[data-ns-copy]');
        if (copy) this.copy(copy);
        const open = event.target.closest('[data-ns-dialog-open]');
        if (open) document.getElementById(open.dataset.nsDialogOpen)?.showModal();
        const close = event.target.closest('[data-ns-dialog-close]');
        if (close) close.closest('dialog')?.close();
      };
      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);
      const cutoff = readAttrJson(this.querySelector('[data-ns-cutoff]')?.dataset.nsCutoff);
      for (const node of this.querySelectorAll('[data-ns-eta]')) {
        // The dates stay on one line ("Nov 4–13", never "Nov" / "4–13"): the line breaks before them instead.
        const [before, range, after] = etaParts(node.dataset.template, etaLabel(Date.now(), node.dataset.min, node.dataset.max, 'en-US', cutoff));
        const dates = document.createElement('span');
        dates.style.whiteSpace = 'nowrap';
        dates.textContent = range;
        node.replaceChildren(before, dates, after);
        node.hidden = false;
      }
      if (this.data.mode === 'backorder') {
        const id = this.form?.querySelector('input[name="id"]')?.value;
        const variant = this.data.variants.find(item => String(item.id) === String(id));
        if (variant) paintBackorder(this, this.form, buyState(variant, 'backorder', null), variant);
      }
    }
    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('click', this.onClick);
    }
    options() {
      return [...this.querySelectorAll('[data-ns-option]')].map(group => group.querySelector('input:checked')?.value ?? '');
    }
    pick() {
      const variant = resolveVariant(this.data.variants, this.options());
      this.show(variant);
      if (variant) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
    }
    show(variant) {
      const sale = buyState(variant, this.data.mode, this.data.planId || null);
      const { price, buyable, reason } = sale;
      const idInput = this.form?.querySelector('input[name="id"]');
      if (idInput && variant) idInput.value = variant.id;
      // Back-order: the plan and its note only for a variant that carries the plan; the others buy at the list price.
      const listOnly = this.data.mode === 'backorder' && !sale.plan;
      for (const group of this.querySelectorAll('[data-ns-option]')) {
        const label = group.querySelector('[data-ns-option-name]');
        if (label) label.textContent = group.querySelector('input:checked')?.value ?? '';
      }
      for (const node of this.querySelectorAll('[data-ns-instock]')) node.hidden = !variant?.available;
      if (this.data.mode === 'backorder') paintBackorder(this, this.form, sale, variant);
      for (const node of this.querySelectorAll('[data-ns-list]')) if (variant) node.textContent = formatMoney(variant.price, this.data.currency);
      for (const node of this.querySelectorAll('[data-ns-price]')) node.textContent = price === null ? '' : formatMoney(price, this.data.currency);
      for (const node of this.querySelectorAll('[data-ns-compare]')) {
        const compare = variant?.compare_at_price;
        node.hidden = !(compare > (variant?.price ?? 0));
        if (!node.hidden) node.textContent = formatMoney(compare, this.data.currency);
      }
      // The sale chip; [data-ns-save] is the Saved-list heart (ns-saved.js), which a variant change leaves alone.
      for (const node of this.querySelectorAll('[data-ns-sale]')) {
        const compare = variant?.compare_at_price;
        node.hidden = !(compare > (variant?.price ?? 0));
        if (!node.hidden) node.textContent = fill(node.dataset.template, { amount: formatMoney(compare - variant.price, this.data.currency) });
      }
      for (const node of this.querySelectorAll('[data-ns-plan-price]')) {
        const tierPrice = sellingPrice(variant, node.dataset.nsPlanPrice);
        node.textContent = tierPrice === null ? '' : formatMoney(tierPrice, this.data.currency);
      }
      for (const node of this.querySelectorAll('[data-ns-plan-save]')) {
        const saving = planSaving(variant, node.dataset.nsPlanSave);
        node.hidden = !(saving > 0);
        if (!node.hidden) node.textContent = fill(node.dataset.template, { amount: formatMoney(saving, this.data.currency) });
      }
      for (const button of document.querySelectorAll(`[data-ns-cta="${this.id}"]`)) {
        button.disabled = !buyable;
        const shortNode = button.querySelector('[data-ns-cta-short]');
        const { template, short } = ctaTemplates(button, shortNode, listOnly);
        const text = ctaText({ price, buyable, reason }, { template, short, soldOut: button.dataset.soldOut, unavailable: button.dataset.unavailable, elsewhere: button.dataset.elsewhere, elsewhereShort: button.dataset.elsewhereShort }, this.data.currency);
        (button.querySelector('[data-ns-cta-label]') || button).textContent = text.long;
        if (shortNode) shortNode.textContent = text.short;
      }
      if (variant?.media) this.querySelector('ns-gallery')?.show(String(variant.media));
      document.dispatchEvent(new CustomEvent('ns:variant', { detail: { price, buyable } }));
    }
    step(delta) {
      const input = this.form?.querySelector('input[name="quantity"]');
      if (!input) return;
      const min = Number(input.min) || 1;
      const max = Number(input.max) || Infinity;
      input.value = String(Math.min(max, Math.max(min, (Number(input.value) || min) + delta)));
    }
    async copy(button) {
      try { await navigator.clipboard.writeText(button.dataset.nsCopy); } catch { return; }
      const label = button.querySelector('[data-ns-copy-label]');
      if (label) label.textContent = button.dataset.copied;
    }
  });
}

if (typeof customElements !== 'undefined' && !customElements.get('ns-gallery')) {
  customElements.define('ns-gallery', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.ns-gallery__track');
      this.thumbs = [...this.querySelectorAll('[data-ns-thumb]')];
      this.dots = [...this.querySelectorAll('.ns-gallery__dot')];
      this.onClick = event => {
        const thumb = event.target.closest('[data-ns-thumb]');
        if (thumb) { event.preventDefault(); this.show(thumb.dataset.nsThumb); }
        const play = event.target.closest('[data-ns-play]');
        if (play) { event.preventDefault(); this.show(play.dataset.nsPlay, true); }
      };
      this.onScroll = () => {
        const index = Math.round(this.track.scrollLeft / Math.max(1, this.track.clientWidth));
        this.mark(index);
      };
      this.addEventListener('click', this.onClick);
      this.track?.addEventListener('scroll', this.onScroll, { passive: true });
      if (this.dataset.start) this.show(this.dataset.start, false, 'instant');
    }
    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.track?.removeEventListener('scroll', this.onScroll);
    }
    slides() { return [...this.querySelectorAll('.ns-gallery__slide')]; }
    show(mediaId, play = false, behavior = 'smooth') {
      const slides = this.slides();
      const index = slides.findIndex(slide => slide.dataset.mediaId === String(mediaId));
      if (index < 0 || !this.track) return;
      this.track.scrollTo({ left: index * this.track.clientWidth, behavior });
      this.mark(index);
      if (play) slides[index].querySelector('video')?.play().catch(() => {});
    }
    mark(index) {
      this.thumbs.forEach((thumb, position) => thumb.setAttribute('aria-current', position === index ? 'true' : 'false'));
      this.dots.forEach((dot, position) => dot.classList.toggle('is-active', position === index));
    }
  });
}

if (typeof customElements !== 'undefined' && !customElements.get('ns-sticky-buy')) {
  customElements.define('ns-sticky-buy', class extends HTMLElement {
    connectedCallback() {
      this.target = document.getElementById(this.dataset.watch);
      if (!this.target) return;
      // A scroll check rather than an IntersectionObserver: a jump past the button (anchor link, End key) never crosses it.
      this.onScroll = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          this.toggleAttribute('data-visible', pastButton(this.target.getBoundingClientRect(), parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--ns-sticky-offset')) || 0));
        });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll);
      this.onScroll();
    }
    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
    }
  });
}

// The combo row lives outside ns-product: it follows the variant on screen through the ns:variant event.
if (typeof document !== 'undefined') {
  document.addEventListener('ns:variant', event => {
    const { price, buyable } = event.detail || {};
    for (const combo of document.querySelectorAll('[data-ns-combo]')) {
      const totals = comboTotals(price, combo.dataset.picks, combo.dataset.percent);
      if (totals) {
        const set = (selector, cents) => { const node = combo.querySelector(selector); if (node) node.textContent = formatMoney(cents); };
        set('[data-ns-combo-main]', price);
        set('[data-ns-combo-was]', totals.was);
        set('[data-ns-combo-now]', totals.now);
      }
      const button = combo.querySelector('[data-ns-combo-add]');
      if (button) {
        button.disabled = !buyable;
        button.textContent = buyable ? button.dataset.add : button.dataset.off;
      }
    }
  });
}

// A video lightbox stops playing when it closes: reloading the iframe resets the player. `close` does not bubble, so listen in the capture phase.
if (typeof document !== 'undefined') {
  document.addEventListener('close', event => {
    const frame = event.target instanceof Element && event.target.matches('dialog.ns-video') ? event.target.querySelector('iframe') : null;
    if (frame) frame.src = frame.src;
  }, true);
}
